<template>
	<div>
		<div class="cs-head">
			<div>
				<h1 class="cs-title">People</h1>
				<p class="cs-sub">Who quotes and who decides. Sales people own quotes and get the notices about them. Finance people decide quotes, keep the price list and get the email when a quote is sent to finance; the email can be switched off per person. Everyone else can read the price list and draft a quote.</p>
			</div>
			<div class="row"><span v-if="changed" class="yn warn">not saved</span><button class="btn-primary" :disabled="!changed || saving" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button></div>
		</div>

		<pe-notice v-if="notice" :tone="notice.tone" :text="notice.text" @close="notice = null" />

		<div class="pe-fin-bar" style="margin-bottom:12px"><span><b>{{ count('sales') }}</b> sales {{ count('sales') === 1 ? 'person' : 'people' }} · <b>{{ count('finance') }}</b> finance · <b>{{ mailCount }}</b> {{ mailCount === 1 ? 'gets' : 'get' }} the finance email<span v-if="finance.length && !mailCount" class="yn warn" style="margin-left:6px">nobody gets the email</span>. You stay finance when you save; nobody can lock finance out.</span>
			<span class="sp"><div class="search"><i class="fa-regular fa-magnifying-glass"></i><input v-model="search" placeholder="Search a name or email" /></div></span></div>

		<div class="tbl-scroll">
			<table class="tbl nochk">
				<thead><tr><th class="sticky-l2">Account</th><th>MC2 role</th><th>Quotes <span class="faint">(sales)</span></th><th>Decides <span class="faint">(finance)</span></th><th>Email <span class="faint">(finance notices)</span></th></tr></thead>
				<tbody v-if="!loading">
					<tr v-for="u in filtered" :key="u.id">
						<td class="sticky-l2"><div class="acct"><span class="avatar">{{ initials(u.name) }}</span><span><span class="acct-name">{{ u.name }}<span v-if="u.id === meId" class="yn inc" style="margin-left:6px">you</span></span><span class="sub">{{ u.email }}</span></span></div></td>
						<td class="faint">{{ u.role || '—' }}</td>
						<td><label class="ck-row" style="border:0;padding:0;display:inline-flex"><input type="checkbox" :checked="sales.includes(u.id)" @change="toggle('sales', u.id)" /><span class="nm">quotes</span></label></td>
						<td><label class="ck-row" style="border:0;padding:0;display:inline-flex"><input type="checkbox" :checked="finance.includes(u.id)" :disabled="u.id === meId" @change="toggle('finance', u.id)" /><span class="nm">decides</span></label></td>
						<td><label class="ck-row" style="border:0;padding:0;display:inline-flex" :class="{ faint: !finance.includes(u.id) }"><input type="checkbox" :checked="finance.includes(u.id) && !quiet.includes(u.id)" :disabled="!finance.includes(u.id)" @change="toggle('quiet', u.id)" /><span class="nm">gets the email</span></label></td>
					</tr>
					<tr v-if="!filtered.length" class="empty"><td colspan="5"><i class="fa-regular fa-folder-open"></i><div>No account matches.</div></td></tr>
				</tbody>
				<tbody v-else><tr v-for="n in 6" :key="n" class="skeleton"><td colspan="5"><span class="sk"></span></td></tr></tbody>
			</table>
		</div>
	</div>
</template>

<script>
import restApi from '../../../api/index.js'
import { PeNotice } from '../../../components/pe'
import { initials, errorText } from '../pe-util'

export default {
	name: 'PricingPeople',
	components: { PeNotice },
	data() { return { users: [], sales: [], finance: [], quiet: [], saved: { sales: [], finance: [], quiet: [] }, meId: null, search: '', loading: false, saving: false, notice: null } },
	computed: {
		filtered() { const q = this.search.trim().toLowerCase(); return this.users.filter(u => !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q)) },
		changed() { const same = (a, b) => a.length === b.length && a.every(x => b.includes(x)); return !same(this.sales, this.saved.sales) || !same(this.finance, this.saved.finance) || !same(this.noMail, this.saved.quiet) },
		// finance people with the email switched off; a quiet tick on someone who is not finance is dropped
		noMail() { return this.quiet.filter(id => this.finance.includes(id)) },
		mailCount() { return this.finance.length - this.noMail.length },
	},
	created() { this.load() },
	beforeRouteLeave(to, from, next) { if (!this.changed) return next(); this.$root.$pePrompt({ title: 'Leave without saving?', text: 'The people lists are not saved.', confirm: 'Leave', danger: true }).then(ok => next(!!ok)) },
	methods: {
		initials,
		count(kind) { return this[kind].length },
		load() {
			this.loading = true
			restApi.get('/pricing/people').then(({ data }) => {
				this.users = data.users || []; this.meId = data.me.id
				this.sales = data.sales.map(u => u.id); this.finance = data.finance.map(u => u.id); this.quiet = data.no_mail || []
				if (!this.finance.includes(this.meId)) this.finance.push(this.meId)
				this.saved = { sales: [...this.sales], finance: [...this.finance], quiet: [...this.noMail] }
			}).catch(err => { this.notice = { tone: 'warn', text: errorText(err, 'Could not load the people') } }).finally(() => { this.loading = false })
		},
		toggle(kind, id) { const i = this[kind].indexOf(id); if (i === -1) this[kind].push(id); else this[kind].splice(i, 1) },
		save() {
			this.saving = true
			restApi.put('/pricing/people', { sales: this.sales, finance: this.finance, no_mail: this.noMail }).then(({ data }) => {
				this.sales = data.sales.map(u => u.id); this.finance = data.finance.map(u => u.id); this.quiet = data.no_mail || []
				this.saved = { sales: [...this.sales], finance: [...this.finance], quiet: [...this.noMail] }
				this.notice = { tone: 'ok', text: 'Saved. ' + this.count('sales') + ' sales ' + (this.count('sales') === 1 ? 'person' : 'people') + ', ' + this.count('finance') + ' finance, ' + this.mailCount + ' ' + (this.mailCount === 1 ? 'gets' : 'get') + ' the finance email.' }
				this.$root.$emit('pe-access-changed')
			}).catch(err => { this.notice = { tone: 'warn', text: errorText(err, 'Save failed') } }).finally(() => { this.saving = false })
		},
	},
}
</script>
